import { z } from 'zod';

export enum GrievanceCategory {
  TXN_FAILED = 'TXN_FAILED',
  WRONG_AMOUNT = 'WRONG_AMOUNT',
  DELAY = 'DELAY',
  DOUBLE_CHARGE = 'DOUBLE_CHARGE',
  CREDIT = 'CREDIT',
  KYC = 'KYC',
  COMMISSION = 'COMMISSION',
  ACCOUNT = 'ACCOUNT',
  OTHER = 'OTHER',
}

export enum GrievanceSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  COMPLIANCE = 'COMPLIANCE',
}

export enum GrievanceStatus {
  OPEN = 'OPEN',
  IN_REVIEW = 'IN_REVIEW',
  WAITING_USER = 'WAITING_USER',
  RESOLVED = 'RESOLVED',
  ESCALATED = 'ESCALATED',
}

export enum GrievanceEventAction {
  CREATED = 'CREATED',
  ASSIGNED = 'ASSIGNED',
  COMMENT = 'COMMENT',
  STATUS_CHANGE = 'STATUS_CHANGE',
  ESCALATED = 'ESCALATED',
  RESOLVED = 'RESOLVED',
}

export const SLA_HOURS_BY_SEVERITY: Record<GrievanceSeverity, number> = {
  [GrievanceSeverity.LOW]: 168,
  [GrievanceSeverity.MEDIUM]: 72,
  [GrievanceSeverity.HIGH]: 24,
  [GrievanceSeverity.COMPLIANCE]: 12,
};

export function slaDurationHours(severity: GrievanceSeverity): number {
  return SLA_HOURS_BY_SEVERITY[severity] ?? SLA_HOURS_BY_SEVERITY[GrievanceSeverity.MEDIUM];
}

export type GrievanceSlaState = 'ok' | 'warn' | 'breach';

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

export function slaChipState(slaDueAt: string, now: number = Date.now()): GrievanceSlaState {
  const deadline = new Date(slaDueAt).getTime();
  if (Number.isNaN(deadline)) return 'ok';
  const msLeft = deadline - now;
  if (msLeft <= 0) return 'breach';
  if (msLeft <= SIX_HOURS_MS) return 'warn';
  return 'ok';
}

export function formatSlaRemaining(slaDueAt: string, now: number = Date.now()): string {
  const deadline = new Date(slaDueAt).getTime();
  if (Number.isNaN(deadline)) return '—';
  const msLeft = deadline - now;
  if (msLeft <= 0) return 'Overdue';
  const hoursLeft = Math.floor(msLeft / (1000 * 60 * 60));
  if (hoursLeft >= 24) {
    const days = Math.floor(hoursLeft / 24);
    const hours = hoursLeft % 24;
    return hours > 0 ? `${days}d ${hours}h left` : `${days}d left`;
  }
  if (hoursLeft >= 1) return `${hoursLeft}h left`;
  const minutesLeft = Math.max(1, Math.floor(msLeft / (1000 * 60)));
  return `${minutesLeft}m left`;
}

export interface GrievanceDTO {
  id: string;
  reporterUid: string;
  reporterName: string | null;
  reporterPhone: string | null;
  reporterEmail: string | null;
  category: GrievanceCategory | string;
  severity: GrievanceSeverity;
  relatedTxnId: string | null;
  subject: string;
  body: string;
  attachments: string | null;
  assigneeUid: string | null;
  status: GrievanceStatus;
  slaDueAt: string;
  resolution: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

export interface GrievanceEventDTO {
  id: string;
  grievanceId: string;
  actorUid: string;
  action: GrievanceEventAction | string;
  payload: string | null;
  createdAt: string;
}

export interface GrievanceWithEvents {
  grievance: GrievanceDTO;
  events: GrievanceEventDTO[];
}

export const CreateGrievanceSchema = z.object({
  category: z.nativeEnum(GrievanceCategory),
  severity: z.nativeEnum(GrievanceSeverity).optional(),
  relatedTxnId: z.string().optional(),
  subject: z.string().min(3, 'Subject is required'),
  body: z.string().min(20, 'Please describe the issue in at least 20 characters'),
  reporterName: z.string().optional(),
  reporterPhone: z.string().optional(),
  reporterEmail: z.string().email().optional().or(z.literal('')),
  attachments: z.array(z.string()).optional(),
  onBehalfOfDealerId: z.string().optional(),
});

export type CreateGrievanceInput = z.infer<typeof CreateGrievanceSchema>;

export const CommentGrievanceSchema = z.object({
  body: z.string().min(1, 'Comment cannot be empty'),
});

export type CommentGrievanceInput = z.infer<typeof CommentGrievanceSchema>;

export const AssignGrievanceSchema = z.object({
  assigneeUid: z.string().min(1),
});

export type AssignGrievanceInput = z.infer<typeof AssignGrievanceSchema>;

export const ResolveGrievanceSchema = z.object({
  resolution: z.string().min(3, 'Resolution note is required'),
});

export type ResolveGrievanceInput = z.infer<typeof ResolveGrievanceSchema>;

export const GRIEVANCE_CATEGORY_LABEL: Record<GrievanceCategory, string> = {
  [GrievanceCategory.TXN_FAILED]: 'Transaction failed',
  [GrievanceCategory.WRONG_AMOUNT]: 'Wrong amount',
  [GrievanceCategory.DELAY]: 'Delay',
  [GrievanceCategory.DOUBLE_CHARGE]: 'Double charge',
  [GrievanceCategory.CREDIT]: 'Credit',
  [GrievanceCategory.KYC]: 'KYC',
  [GrievanceCategory.COMMISSION]: 'Commission',
  [GrievanceCategory.ACCOUNT]: 'Account',
  [GrievanceCategory.OTHER]: 'Other',
};

export const GRIEVANCE_SEVERITY_LABEL: Record<GrievanceSeverity, string> = {
  [GrievanceSeverity.LOW]: 'Minor — just FYI',
  [GrievanceSeverity.MEDIUM]: 'Impacting — workaround is slow',
  [GrievanceSeverity.HIGH]: 'Critical — I can’t do my job',
  [GrievanceSeverity.COMPLIANCE]: 'Compliance / regulatory',
};

export const GRIEVANCE_STATUS_LABEL: Record<GrievanceStatus, string> = {
  [GrievanceStatus.OPEN]: 'Open',
  [GrievanceStatus.IN_REVIEW]: 'In review',
  [GrievanceStatus.WAITING_USER]: 'Waiting on you',
  [GrievanceStatus.RESOLVED]: 'Resolved',
  [GrievanceStatus.ESCALATED]: 'Escalated',
};
